// reelEngine.js — our own license-free reel playback engine.
// Owns the master clock (Web Audio AudioContext.currentTime, sample-accurate) and
// schedules every audio clip across tracks with per-clip gain + fades. The React
// component draws the canvas/subtitles by subscribing to onTime. Replaces the
// Remotion <Player> for the reel/editor.

// A track contributes audio unless it's the visual base ("video") or a visual
// overlay ("overlay") — those are composited on the canvas, not decoded as audio.
const isAudioTrack = (t) => t.kind !== "video" && t.kind !== "overlay";

export class ReelEngine {
  constructor(edl, { onTime, onPlayStateChange } = {}) {
    this.edl = edl;
    this.onTime = onTime || (() => {});
    this.onPlayStateChange = onPlayStateChange || (() => {});
    this.duration = this._computeDuration(edl);
    this.loop = true;
    this.ctx = null;
    this.masterGain = null;
    this.trackGains = {};   // trackId -> GainNode
    this.buffers = {};      // clipId -> AudioBuffer
    this.sources = [];      // live AudioBufferSourceNodes
    this._playing = false;
    this._pausedAt = 0;     // seconds
    this._ctxStart = 0;     // ctx.currentTime captured at play()
    this._raf = null;
  }

  async load() {
    if (this.ctx) return;
    const AC = window.AudioContext || window.webkitAudioContext;
    this.ctx = new AC();
    this.masterGain = this.ctx.createGain();
    this.masterGain.connect(this.ctx.destination);
    for (const t of this.edl.tracks) {
      const g = this.ctx.createGain();
      g.gain.value = t.gain ?? 1;
      g.connect(this.masterGain);
      this.trackGains[t.id] = g;
    }
    const audioClips = this.edl.tracks
      .filter(isAudioTrack)
      .flatMap((t) => t.clips);
    await Promise.all(audioClips.map(async (c) => {
      if (!c.src || this.buffers[c.id]) return;
      try {
        const resp = await fetch(c.src);
        const arr = await resp.arrayBuffer();
        this.buffers[c.id] = await this.ctx.decodeAudioData(arr);
      } catch { /* missing/undecodable clip → silent */ }
    }));
  }

  _computeDuration(edl) {
    let d = edl?.totalSec || 0;
    for (const t of edl?.tracks || []) {
      for (const c of t.clips || []) d = Math.max(d, c.start + c.duration);
    }
    return d;
  }

  currentTime() {
    if (this._playing) return Math.min(this.duration, this._pausedAt + (this.ctx.currentTime - this._ctxStart));
    return this._pausedAt;
  }

  getBuffer(clipId) { return this.buffers[clipId]; }

  /** Decode any audio clips not yet buffered (call after adding clips). */
  async ensureBuffers() {
    if (!this.ctx) await this.load();
    const audioClips = this.edl.tracks.filter(isAudioTrack).flatMap((t) => t.clips);
    await Promise.all(audioClips.map(async (c) => {
      if (!c.src || this.buffers[c.id]) return;
      try {
        const resp = await fetch(c.src);
        const arr = await resp.arrayBuffer();
        this.buffers[c.id] = await this.ctx.decodeAudioData(arr);
      } catch { /* skip */ }
    }));
  }

  /** Apply an edited EDL: new duration, ensure track gains exist, reschedule. */
  update(edl) {
    this.edl = edl;
    this.duration = this._computeDuration(edl);
    if (this.ctx) {
      const soloed = (edl.tracks || []).some((t) => t.solo);
      for (const t of edl.tracks) {
        const silenced = t.muted || (soloed && !t.solo);
        if (!this.trackGains[t.id]) {
          const g = this.ctx.createGain();
          g.gain.value = silenced ? 0 : (t.gain ?? 1);
          g.connect(this.masterGain);
          this.trackGains[t.id] = g;
        } else {
          this.trackGains[t.id].gain.value = silenced ? 0 : (t.gain ?? 1);
        }
      }
      this.setMasterGain(edl.master_gain ?? 1);
    }
    if (this._playing) {
      const t = this.currentTime();
      this._pausedAt = t;
      this._ctxStart = this.ctx.currentTime;
      this._scheduleFrom(t);
    }
  }

  _stopSources() {
    for (const s of this.sources) { try { s.stop(); s.disconnect(); } catch {} }
    this.sources = [];
  }

  _scheduleFrom(fromSec) {
    this._stopSources();
    const now = this.ctx.currentTime;
    for (const track of this.edl.tracks) {
      if (!isAudioTrack(track)) continue;
      const trackGain = this.trackGains[track.id];
      for (const clip of track.clips) {
        if (clip.disabled) continue;                          // muted/disabled clip
        const buf = this.buffers[clip.id];
        if (!buf) continue;
        if (clip.start + clip.duration <= fromSec) continue; // fully past
        const into = Math.max(0, fromSec - clip.start);      // secs already elapsed in clip
        const when = now + Math.max(0, clip.start - fromSec);
        const offset = (clip.in || 0) + into;
        const playDur = Math.max(0, clip.duration - into);
        if (playDur <= 0) continue;
        const src = this.ctx.createBufferSource();
        src.buffer = buf;
        const g = this.ctx.createGain();
        const gain = clip.gain ?? 1;
        g.gain.value = gain;
        if (clip.fade_in) { g.gain.setValueAtTime(0, when); g.gain.linearRampToValueAtTime(gain, when + clip.fade_in); }
        if (clip.fade_out) {
          const foStart = when + playDur - clip.fade_out;
          g.gain.setValueAtTime(gain, Math.max(when, foStart));
          g.gain.linearRampToValueAtTime(0.0001, when + playDur);
        }
        src.connect(g); g.connect(trackGain);
        try { src.start(when, offset, playDur); } catch {}
        this.sources.push(src);
      }
    }
  }

  _tick = () => {
    const t = this.currentTime();
    this.onTime(t);
    if (t >= this.duration - 0.012) {
      if (this.loop) { this.seek(0); }
      else { this.pause(); return; }
    }
    if (this._playing) this._raf = requestAnimationFrame(this._tick);
  };

  async play() {
    await this.load();
    if (this.ctx.state === "suspended") { try { await this.ctx.resume(); } catch {} }
    if (this._playing) return;
    if (this._pausedAt >= this.duration - 0.012) this._pausedAt = 0;
    this._ctxStart = this.ctx.currentTime;
    this._scheduleFrom(this._pausedAt);
    this._playing = true;
    this.onPlayStateChange(true);
    this._raf = requestAnimationFrame(this._tick);
  }

  pause() {
    if (!this._playing) return;
    this._pausedAt = this.currentTime();
    this._stopSources();
    this._playing = false;
    if (this._raf) cancelAnimationFrame(this._raf);
    this.onPlayStateChange(false);
  }

  toggle() { return this._playing ? this.pause() : this.play(); }

  seek(sec) {
    const t = Math.max(0, Math.min(this.duration, sec));
    if (this._playing) {
      this._pausedAt = t;
      this._ctxStart = this.ctx.currentTime;
      this._scheduleFrom(t);
    } else {
      this._pausedAt = t;
    }
    this.onTime(t);
  }

  setMasterGain(v) { if (this.masterGain) this.masterGain.gain.value = v; }
  setTrackGain(trackId, v) { if (this.trackGains[trackId]) this.trackGains[trackId].gain.value = v; }
  get playing() { return this._playing; }

  dispose() {
    this._stopSources();
    if (this._raf) cancelAnimationFrame(this._raf);
    if (this.ctx) { try { this.ctx.close(); } catch {} }
  }
}
